/**
 * Clarik AI — floating typed-chat assistant mounted globally.
 *
 * Spoken voice is hard-off for beta (Task #427): the kill-switch in
 * server/voice-assistant/beta-kill.ts forces every session to the typed
 * "disabled" provider, so the mic / VAD / TTS path is gone from this widget
 * and the floating button is a typed-chat launcher (Sparkles) only.
 *
 * The Realtime (WebRTC) engine below is kept compiled but dormant — it is
 * never reached while the kill-switch is on. When voice is restored, flip the
 * kill-switch and `provider === "realtime"` sessions will engage it again.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useVoiceAssistantState } from "@/contexts/voice-assistant-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X, Loader2, Send, Sparkles,
  Paperclip, Image as ImageIcon, Check, Users, AlertCircle,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { apiRequest, queryClient, withCsrfHeader } from "@/lib/queryClient";
import { parseApiError } from "@/lib/ai-error";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

type ProviderKind = "realtime" | "disabled";

type VoiceAssistantConfig = {
  settings: {
    enabled: boolean;
    provider: ProviderKind;
    allowActions: boolean;
    enabledRoles: string[];
    voicePersona: string;
    mobileButtonMode?: "drawer" | "voice_direct" | "voice_panel";
  } | null;
  role: string;
  isSuperAdmin: boolean;
  coachEnabled: boolean;
  toolSurface: Array<{ name: string; description: string; isWrite: boolean }>;
};

interface AssignmentOptions {
  regions: { id: string; name: string }[];
  clientTypes: { id: string; name: string }[];
  talent: { id: string; name: string }[];
  salesReps: { userId: string; fullName: string }[];
  isSalesRep: boolean;
}

interface WidgetScreenshotDraft {
  fields: Record<string, string | null | undefined>;
  regionId: string;
  clientType: string;
  talentId: string;
  assignedRepUserId: string;
  idempotencyKey: string;
  notesIncludes: { label: string; value: string }[];
  // Set when this lead card was seeded from an uploaded attachment (Task
  // #246). Forwarded to the import route so the attachment is linked to the
  // created lead and hooked into drip auto-enrollment.
  attachmentId?: string;
}

/** Per-row data for the bulk import card. */
interface BulkRow {
  fields: Record<string, string | null | undefined>;
  notesIncludes: { label: string; value: string }[];
}

/** Draft state for the multi-record bulk-screenshot turn. */
interface WidgetBulkScreenshotDraft {
  rows: BulkRow[];
  recordType: "lead" | "client";
  // Shared assignment across all rows.
  regionId: string;
  clientType: string;
  talentId: string;
  assignedRepUserId: string;
  attachmentId?: string;
}

type Turn =
  | { id: string; speaker: "user"; text: string }
  | { id: string; speaker: "assistant"; text: string }
  | { id: string; speaker: "system"; text: string; link?: { label: string; href: string } }
  | {
      id: string;
      speaker: "screenshot";
      summary: string;
      draft: WidgetScreenshotDraft;
      status: "editing" | "importing" | "imported" | "cancelled";
      createdLeadId?: string;
      error?: string;
    }
  | {
      id: string;
      speaker: "bulk-screenshot";
      summary: string;
      draft: WidgetBulkScreenshotDraft;
      status: "editing" | "importing" | "imported" | "cancelled";
      importedCount?: number;
      failedCount?: number;
      /** Per-row failures returned by the import-batch endpoint. */
      failedRows?: Array<{ index: number; error: string }>;
      error?: string;
    };

const MAX_ATTACHMENT_BYTES = 6.5 * 1024 * 1024;

function makeIdempotencyKey(): string {
  return `scr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

interface SessionInfo {
  sessionId: string;
  provider: ProviderKind;
  voicePersona: string;
  allowActions: boolean;
  crossWorkspace: boolean;
}

function uid() {
  return Math.random().toString(36).slice(2);
}

export function VoiceAssistantWidget() {
  const { toast } = useToast();
  const [location, navigate] = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();

  const { data: config, isLoading } = useQuery<VoiceAssistantConfig>({
    queryKey: ["/api/voice-assistant/config"],
    retry: 2,
    retryDelay: 1500,
    // Cache for 5 min so page navigation doesn't re-fetch on every route change
    // and the FAB appears instantly from cache on subsequent visits.
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // Coach pending pings badge
  const { data: pendingPings = [] } = useQuery<Array<{ id: string; kind: string; openingLine: string | null }>>({
    queryKey: ["/api/coach/pings"],
    enabled: !!config?.settings?.enabled,
    refetchInterval: 60_000,
    retry: false,
    select: (raw: any) =>
      Array.isArray(raw?.pings) ? raw.pings : Array.isArray(raw) ? raw : [],
  });
  const pendingCount = Array.isArray(pendingPings) ? pendingPings.length : 0;

  // Sandbox engaged badge (amber)
  const [sandboxEngaged, setSandboxEngaged] = useState(false);
  const { data: sandboxData } = useQuery<{ items: any[] }>({
    queryKey: ["/api/lead-gen/sandbox"],
    enabled: !!config?.settings?.enabled && sandboxEngaged,
    refetchInterval: sandboxEngaged ? 4_000 : false,
    retry: false,
    select: (raw: any) => ({ items: Array.isArray(raw?.items) ? raw.items : [] }),
  });
  const sandboxCount = sandboxData?.items?.filter((i: any) => !i.ignored).length ?? 0;
  useEffect(() => {
    if (sandboxEngaged && sandboxCount === 0 && sandboxData !== undefined) {
      setSandboxEngaged(false);
    }
  }, [sandboxEngaged, sandboxCount, sandboxData]);

  // ── Shared open/voice state — lifted so mobile nav center button can read it ──
  // Task #117 — Coach mode is no longer a user-toggleable segmented control.
  // It is driven by *where* the assistant is opened: when embedded inside the
  // Calendar Hub's right-hand side panel, coach mode is forced on; anywhere
  // else (the floating FAB on any other page) the assistant defaults to work
  // mode. The old `assistant_mode` localStorage key is intentionally retired.
  const { open, setOpen, voiceEngaged, setVoiceEngaged, tourPanelOpen, setAssistantEnabled, calendarPanelMounted, calendarCoachTarget, openCalendarCoachPanel } = useVoiceAssistantState();

  // ── Core state ────────────────────────────────────────────────────
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [assignmentOptions, setAssignmentOptions] = useState<AssignmentOptions | null>(null);

  // Refs stable across async VAD callbacks
  const sessionRef = useRef<SessionInfo | null>(null);
  const turnsRef = useRef<Turn[]>([]);
  const realtimeRef = useRef<RealtimeHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const optionsFetchedRef = useRef(false);
  // Guards the one-shot coach auto-trigger per session so re-renders or
  // strict-mode double-invocations don't fire it twice.
  const coachAutoTriggeredRef = useRef(false);
  // 5-minute grace timer — clears turns after the session ends and the panel
  // stays closed. Cancelled if the user re-opens before the timer fires.
  const clearHistoryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { turnsRef.current = turns; }, [turns]);
  // Stable ref so the async session-open effect can read the latest pings
  // without declaring them as a dependency (which would re-start sessions
  // every time the polling refetch runs).
  const pendingPingsRef = useRef(pendingPings);
  useEffect(() => { pendingPingsRef.current = pendingPings; }, [pendingPings]);

  const isVisible = useMemo(() => {
    if (!config?.settings?.enabled) return false;
    if (!config.settings.enabledRoles?.includes(config.role)) return false;
    return true;
  }, [config]);

  // Task #116 — Publish assistant-enabled to context so the Tour Planning
  // drawer can decide whether to claim the shared morph layoutId. The drawer
  // must NOT hold the id when the assistant is disabled (no orb peer exists).
  useEffect(() => {
    setAssistantEnabled(isVisible);
  }, [isVisible, setAssistantEnabled]);

  // Task #116 — First-paint morph source. On direct URL arrival with the
  // drawer already open, widget + master-schedule mount in the same commit.
  // Holding the orb visible for one extra frame gives Framer a real bounding
  // box to morph from. Stays true for the rest of the widget's lifetime, so
  // normal toggles aren't delayed.
  const [morphReady, setMorphReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMorphReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, busy]);

  // Task #117 — embedded mode detection. When the Calendar Hub's coach view
  // has registered a portal target and the assistant is enabled, the widget
  // mounts its panel inline inside that target instead of as a floating
  // drawer over the page. The FAB is suppressed and the assistant is forced
  // into coach mode for the duration of the embed.
  const onCalendars = /(^|\/)calendars(\/|$|\?)/.test(location);
  const onMasterSchedule = /(^|\/)master-schedule(\/|$|\?)/.test(location);
  const isEmbedded = !!(onCalendars && calendarCoachTarget && isVisible && !isMobile);
  // Coach mode is driven entirely by context now (Task #117). Embedded inside
  // the Calendar Hub coach view ⇒ coach on; everywhere else ⇒ work mode.
  const coachMode = isEmbedded;
  // Derive the active AI engine surface for tool segregation.
  // Coach takes priority over tour (embedded calendar view wins).
  // Tour mode is ONLY active when the user is actually on Master Schedule
  // with the tour panel open — tourPanelOpen persists in localStorage, so
  // without the onMasterSchedule guard it would bleed onto every page.
  const activeSurface: "assistant" | "tour" | "coach" =
    coachMode ? "coach" : (onMasterSchedule && tourPanelOpen) ? "tour" : "assistant";

  // ── Current-view awareness (Task #245) ───────────────────────────
  // Tell the assistant where the user is and which record (if any) is open,
  // so "this lead/event/invoice" resolves without an extra lookup tool call.
  const currentView = useMemo(() => {
    const path = location.split("?")[0] || "/";
    const segs = path.split("/").filter(Boolean);
    const typeMap: Record<string, string> = {
      leads: "lead",
      clients: "client",
      invoices: "invoice",
      "master-schedule": "event",
      events: "event",
      schedule: "event",
    };
    let recordType: string | undefined;
    let recordId: string | undefined;
    if (segs.length >= 2 && typeMap[segs[0]] && segs[1] !== "new") {
      recordType = typeMap[segs[0]];
      recordId = segs[1];
    }
    return { path, recordType, recordId };
  }, [location]);
  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;

  // ── Session open / close ─────────────────────────────────────────
  useEffect(() => {
    if ((!open && !isEmbedded) || session) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest("POST", "/api/voice-assistant/session/start", {});
        const j = await res.json();
        if (cancelled) return;
        // Sync the ref immediately so sendUserText (called below in the same
        // async tick) can read the session without waiting for a re-render.
        setSession(j);
        sessionRef.current = j;
        // Preserve existing history during the 5-min grace window.
        // Only add the hello turn when there's no prior conversation.
        if (turnsRef.current.length === 0) {
          const firstPing = pendingPingsRef.current[0];
          // When a coach ping is pending, replace the generic greeting with a
          // short placeholder so the panel isn't blank while the auto-trigger
          // fires. The actual coach opening line arrives via sendUserText below.
          const greetingText = firstPing
            ? "It's time to check in with your coach!"
            : hello(j.provider, config?.role ?? "user");
          const wsSlug = location.match(/^\/w\/([^/]+)/)?.[1];
          const coachHref = wsSlug ? `/w/${wsSlug}/calendar?tab=coach` : "/calendar?tab=coach";
          const greetingTurn: Turn = firstPing
            ? { id: uid(), speaker: "system", text: greetingText, link: { label: "Open Coach AI →", href: coachHref } }
            : { id: uid(), speaker: "system", text: greetingText };
          setTurns([greetingTurn]);

          // Auto-fire the coach opening line on the typed/text path only
          // (realtime already speaks proactively). A sentinel is sent without
          // showing a user turn so the assistant fires with the system-prompt-
          // injected opening line immediately, without the user typing first.
          // coachModeOverride:true ensures the server injects the coach block
          // even when the floating panel (not the embedded coach panel) fires.
          if (firstPing && j.provider !== "realtime" && !coachAutoTriggeredRef.current) {
            coachAutoTriggeredRef.current = true;
            // Run async without blocking session setup. The sentinel text is
            // never pushed as a user turn — only the assistant response appears.
            // After the opening check-in plays, mark the ping delivered so the
            // badge clears immediately (no wait for the 60s poll interval).
            void sendUserText("__coach_open__", { coachModeOverride: true }).then(() => {
              void apiRequest("POST", `/api/coach/pings/${firstPing.id}/delivered`, {}).catch(() => {});
              void queryClient.invalidateQueries({ queryKey: ["/api/coach/pings"] });
            });
          }
        }
        if (j.provider === "realtime") {
          await openRealtime(j);
        }
      } catch (err: any) {
        const { message, status } = parseApiError(err);
        const friendly =
          status === 503
            ? (message || "The assistant is busy right now. Please try again in a moment.")
            : (message || "Couldn't reach the assistant. Please try again.");
        toast({ title: "Couldn't open assistant", description: friendly, variant: "destructive" });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEmbedded]);

  // Teardown + 5-min grace period for chat history.
  // When the panel is closed AND voice is disengaged, tear down the active
  // voice state immediately, but wait 5 minutes before clearing the turn
  // history. Re-opening the panel or re-engaging voice before the timer
  // fires cancels the clear so the user sees their conversation again.
  useEffect(() => {
    if (open || voiceEngaged || isEmbedded) {
      // User came back — cancel any pending history wipe
      if (clearHistoryTimerRef.current) {
        clearTimeout(clearHistoryTimerRef.current);
        clearHistoryTimerRef.current = null;
      }
      return;
    }
    // Panel closed & not engaged → stop voice/session but keep turns
    teardown();
    if (clearHistoryTimerRef.current) clearTimeout(clearHistoryTimerRef.current);
    clearHistoryTimerRef.current = setTimeout(() => {
      setTurns([]);
      turnsRef.current = [];
      clearHistoryTimerRef.current = null;
    }, 5 * 60 * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, voiceEngaged, isEmbedded]);


  function teardown() {
    if (realtimeRef.current) { realtimeRef.current.stop(); realtimeRef.current = null; }
    const s = sessionRef.current;
    if (s) {
      void apiRequest("POST", "/api/voice-assistant/session/end", { sessionId: s.sessionId }).catch(() => {});
      setSession(null);
      sessionRef.current = null;
    }
    // NOTE: turns are intentionally NOT cleared here. They are kept for a
    // 5-minute grace period (see the teardown effect above) so the user can
    // reopen the panel and still see the conversation.
    setBusy(false);
    setVoiceEngaged(false);
    // Reset auto-trigger guard so a new session (after the grace window
    // expires and turns are wiped) can fire the opening again.
    coachAutoTriggeredRef.current = false;
  }

  // ── Realtime engine ───────────────────────────────────────────────
  async function openRealtime(s: SessionInfo) {
    try {
      const handle = await startRealtimeEngine({
        sessionId: s.sessionId,
        onTranscript: (text, who) => pushTurn({ id: uid(), speaker: who, text }),
        onError: (msg) => pushTurn({ id: uid(), speaker: "system", text: `(${msg})` }),
      });
      realtimeRef.current = handle;
      setVoiceEngaged(true);
      setOpen(false);
    } catch (err: any) {
      pushTurn({ id: uid(), speaker: "system", text: `Couldn't start realtime — ${err?.message ?? "unknown error"}.` });
    }
  }


  // ── Typed chat ───────────────────────────────────────────────────
  function pushTurn(t: Turn) {
    setTurns((cur) => [...cur, t]);
  }

  async function sendUserText(text: string, opts?: { coachModeOverride?: boolean }) {
    const s = sessionRef.current;
    if (!s) return;
    setBusy(true);

    const history = turnsRef.current
      .filter((t): t is Extract<Turn, { speaker: "user" | "assistant" }> => t.speaker === "user" || t.speaker === "assistant")
      .slice(-10)
      .map((t) => ({ role: t.speaker, content: t.text }));
    const body = {
      sessionId: s.sessionId, userText: text, history,
      coachModeOverride: opts?.coachModeOverride ?? !!coachMode,
      surface: activeSurface,
      currentView: currentViewRef.current,
    };

    // Push an empty assistant bubble immediately so we can append deltas
    // into it as they stream in.
    const assistantTurnId = uid();
    pushTurn({ id: assistantTurnId, speaker: "assistant", text: "" });
    const appendDelta = (delta: string) => {
      setTurns((cur) =>
        cur.map((t) =>
          t.id === assistantTurnId && t.speaker === "assistant"
            ? { ...t, text: (t.text || "") + delta }
            : t,
        ),
      );
    };
    const replaceText = (full: string) => {
      setTurns((cur) =>
        cur.map((t) =>
          t.id === assistantTurnId && t.speaker === "assistant"
            ? { ...t, text: full }
            : t,
        ),
      );
    };

    let finalText = "";
    let navigateTo: string | null = null;
    let streamedOk = false;

    try {
      // Attach CSRF header — apiRequest does this for us elsewhere, so mirror it.
      const csrfRes = await fetch("/api/csrf", { credentials: "include" }).catch(() => null);
      const csrf = csrfRes && csrfRes.ok ? (await csrfRes.json()).csrfToken : "";
      const resp = await fetch("/api/voice-assistant/chat/respond-stream", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "x-csrf-token": csrf } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          let evt: any;
          try { evt = JSON.parse(trimmed); } catch { continue; }
          if (evt.type === "text_delta" && typeof evt.delta === "string") {
            appendDelta(evt.delta);
            finalText += evt.delta;
          } else if (evt.type === "done") {
            if (typeof evt.text === "string" && evt.text.trim()) {
              finalText = evt.text;
              replaceText(finalText);
            }
            if (typeof evt.navigateTo === "string") navigateTo = evt.navigateTo;
          } else if (evt.type === "error") {
            throw new Error(evt.message || "stream error");
          }
        }
      }
      streamedOk = true;
    } catch (streamErr) {
      // Fall back to the non-streaming endpoint so a transient stream
      // failure doesn't lose the user's turn.
      try {
        const r = await apiRequest("POST", "/api/voice-assistant/chat/respond", body);
        const j = await r.json();
        finalText = (j?.text ?? "").trim() || "(no reply)";
        replaceText(finalText);
        if (j?.navigateTo && typeof j.navigateTo === "string") navigateTo = j.navigateTo;
        streamedOk = true;
      } catch (err: any) {
        replaceText(`(error — ${err?.message ?? "respond failed"})`);
      }
    }

    try {
      if (streamedOk && navigateTo) {
        setSandboxEngaged(true);
        setOpen(false);
        navigate(navigateTo);
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitTyped() {
    const t = typed.trim();
    if (!t || !session || busy) return;
    setTyped("");
    pushTurn({ id: uid(), speaker: "user", text: t });
    await sendUserText(t);
  }

  // ── Screenshot → lead ─────────────────────────────────────────────
  async function fetchAssignmentOptions(): Promise<AssignmentOptions | null> {
    if (optionsFetchedRef.current && assignmentOptions) return assignmentOptions;
    try {
      const r = await apiRequest("GET", "/api/voice-assistant/screenshot/options");
      if (!r.ok) return null;
      const data = (await r.json()) as AssignmentOptions;
      optionsFetchedRef.current = true;
      setAssignmentOptions(data);
      return data;
    } catch {
      return null;
    }
  }

  function openFilePicker() {
    if (extracting || busy) return;
    fileInputRef.current?.click();
  }

  async function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    const isSupported =
      file.type.startsWith("image/") ||
      file.type === "application/pdf" ||
      file.type.startsWith("text/") ||
      file.type === "application/json" ||
      file.type === "application/xml";
    if (!isSupported) {
      toast({ title: "Unsupported file", description: "Upload an image, PDF, or text file.", variant: "destructive" });
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast({ title: "File too large", description: "Max 6 MB. Try a smaller file.", variant: "destructive" });
      return;
    }
    const s = session;
    if (!s) {
      toast({ title: "No active session", description: "Start a conversation first.", variant: "destructive" });
      return;
    }
    setOpen(true);
    setExtracting(true);
    pushTurn({ id: uid(), speaker: "system", text: `Reading ${file.name}…` });
    try {
      const [fileBase64, opts] = await Promise.all([
        blobToBase64(file),
        fetchAssignmentOptions(),
      ]);
      const r = await apiRequest("POST", "/api/voice-assistant/attachment/upload", {
        sessionId: s.sessionId,
        fileBase64,
        mimeType: file.type || "application/octet-stream",
        fileName: file.name,
      });
      const data = (await r.json()) as {
        attachmentId: string;
        fileName: string;
        summary: string;
        hasText: boolean;
        looksLikeLead: boolean;
        recordType: "lead" | "client" | null;
        recordCount: number;
        records: Array<{ mappedFields: Record<string, string | null>; extras: { label: string; value: string }[] }>;
        leadAssessment: { mappedFields: Record<string, string | null>; extras: { label: string; value: string }[] } | null;
      };
      pushTurn({
        id: uid(),
        speaker: "assistant",
        text: data.summary || `I've read ${data.fileName}. Ask me anything about it.`,
      });
      if (data.looksLikeLead && data.leadAssessment) {
        const regionId = opts?.regions.length === 1 ? opts.regions[0].id : "";
        const clientType = opts?.clientTypes.length === 1 ? opts.clientTypes[0].name : "";
        const talentId = opts?.talent.length === 1 ? opts.talent[0].id : "";
        // Route to the bulk-screenshot card when:
        //   - multiple records detected (>1 rows), OR
        //   - single record detected as a client (legacy card is lead-only)
        const useBulkCard =
          (data.recordCount > 1 && data.records.length > 1) ||
          data.recordType === "client";

        if (useBulkCard) {
          const rows: BulkRow[] = (data.records.length > 0 ? data.records : [data.leadAssessment!]).map((rec) => ({
            fields: { ...rec.mappedFields },
            notesIncludes: rec.extras ?? [],
          }));
          const noun = data.recordType === "client" ? "client" : "lead";
          const plural = rows.length !== 1 ? "s" : "";
          pushTurn({
            id: uid(),
            speaker: "bulk-screenshot",
            summary: rows.length === 1
              ? `This looks like a ${noun} — review and confirm to import.`
              : `Found ${rows.length} ${noun}${plural} — review and import.`,
            draft: {
              rows,
              recordType: data.recordType ?? "lead",
              regionId,
              clientType,
              talentId,
              assignedRepUserId: "",
              attachmentId: data.attachmentId,
            },
            status: "editing",
          });
        } else {
          pushTurn({
            id: uid(),
            speaker: "screenshot",
            summary: "This looks like a lead — review and confirm to create it.",
            draft: {
              fields: { ...data.leadAssessment.mappedFields },
              regionId,
              clientType,
              talentId,
              assignedRepUserId: "",
              idempotencyKey: makeIdempotencyKey(),
              notesIncludes: data.leadAssessment.extras ?? [],
              attachmentId: data.attachmentId,
            },
            status: "editing",
          });
        }
      }
    } catch (err: any) {
      pushTurn({ id: uid(), speaker: "system", text: `Couldn't read that file — ${err?.message ?? "try again."}` });
    } finally {
      setExtracting(false);
    }
  }

  function patchScreenshotTurn(turnId: string, patch: Partial<Extract<Turn, { speaker: "screenshot" }>>) {
    setTurns((cur) => cur.map((t) => (t.id === turnId ? { ...t, ...patch } as Turn : t)));
  }

  function patchBulkScreenshotTurn(turnId: string, patch: Partial<Extract<Turn, { speaker: "bulk-screenshot" }>>) {
    setTurns((cur) => cur.map((t) => (t.id === turnId ? { ...t, ...patch } as Turn : t)));
  }

  function updateBulkAssignment(
    turnId: string,
    key: "regionId" | "clientType" | "talentId" | "assignedRepUserId",
    value: string,
  ) {
    setTurns((cur) =>
      cur.map((t) => {
        if (t.id !== turnId || t.speaker !== "bulk-screenshot") return t;
        return { ...t, draft: { ...t.draft, [key]: value } } as Turn;
      }),
    );
  }

  function updateBulkRowField(
    turnId: string,
    rowIndex: number,
    field: string,
    value: string,
  ) {
    setTurns((cur) =>
      cur.map((t) => {
        if (t.id !== turnId || t.speaker !== "bulk-screenshot") return t;
        const rows = t.draft.rows.map((row, i) =>
          i === rowIndex
            ? { ...row, fields: { ...row.fields, [field]: value || null } }
            : row,
        );
        return { ...t, draft: { ...t.draft, rows } } as Turn;
      }),
    );
  }

  function updateBulkRecordType(turnId: string, value: "lead" | "client") {
    setTurns((cur) =>
      cur.map((t) => {
        if (t.id !== turnId || t.speaker !== "bulk-screenshot") return t;
        return { ...t, draft: { ...t.draft, recordType: value } } as Turn;
      }),
    );
  }

  async function importBulkScreenshotTurn(turnId: string) {
    if (!session) return;
    const turn = turns.find((t) => t.id === turnId) as Extract<Turn, { speaker: "bulk-screenshot" }> | undefined;
    if (!turn || turn.status !== "editing") return;
    if (!turn.draft.regionId) {
      toast({ title: "Region required", description: "Please select a region.", variant: "destructive" });
      return;
    }
    if (!turn.draft.clientType) {
      toast({ title: "Client type required", description: "Please select a client type.", variant: "destructive" });
      return;
    }
    if (assignmentOptions && assignmentOptions.talent.length > 1 && !turn.draft.talentId) {
      toast({ title: "Talent required", description: "Please select a talent.", variant: "destructive" });
      return;
    }
    if (assignmentOptions && !assignmentOptions.isSalesRep && !turn.draft.assignedRepUserId) {
      toast({ title: "Sales rep required", description: "Please assign a sales rep.", variant: "destructive" });
      return;
    }
    patchBulkScreenshotTurn(turnId, { status: "importing" });
    try {
      const r = await apiRequest("POST", "/api/voice-assistant/screenshot/import-batch", {
        sessionId: session.sessionId,
        recordType: turn.draft.recordType,
        rows: turn.draft.rows.map((row) => ({
          fields: row.fields,
          notesIncludes: row.notesIncludes.filter((x) => x.value.trim().length > 0),
        })),
        regionId: turn.draft.regionId,
        clientType: turn.draft.clientType,
        talentId: turn.draft.talentId || undefined,
        assignedRepUserId: turn.draft.assignedRepUserId || undefined,
        attachmentId: turn.draft.attachmentId,
      });
      const result = (await r.json()) as {
        imported: number;
        failed: number;
        total: number;
        results: Array<{ index: number; success: boolean; error?: string }>;
      };
      const failedRows = (result.results ?? [])
        .filter((r) => !r.success)
        .map((r) => ({ index: r.index, error: r.error ?? "Unknown error" }));
      patchBulkScreenshotTurn(turnId, {
        status: "imported",
        importedCount: result.imported,
        failedCount: result.failed,
        failedRows,
      });
      // Invalidate both leads and clients since we may have created either.
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      const noun = turn.draft.recordType === "client" ? "clients" : "leads";
      if (result.failed > 0) {
        toast({
          title: `${result.imported} of ${result.total} ${noun} imported`,
          description: `${result.failed} record${result.failed !== 1 ? "s" : ""} failed to import.`,
          variant: "destructive",
        });
      } else {
        toast({ title: `${result.imported} ${noun} imported`, description: `All records imported successfully.` });
      }
    } catch (err: any) {
      patchBulkScreenshotTurn(turnId, { status: "editing", error: err?.message ?? "Import failed." });
      toast({ title: "Couldn't import", description: err?.message ?? "Try again.", variant: "destructive" });
    }
  }

  function updateScreenshotAssignment(
    turnId: string,
    key: "regionId" | "clientType" | "talentId" | "assignedRepUserId",
    value: string,
  ) {
    setTurns((cur) =>
      cur.map((t) => {
        if (t.id !== turnId || t.speaker !== "screenshot") return t;
        return { ...t, draft: { ...t.draft, [key]: value } } as Turn;
      }),
    );
  }

  async function importScreenshotTurn(turnId: string) {
    if (!session) return;
    const turn = turns.find((t) => t.id === turnId) as Extract<Turn, { speaker: "screenshot" }> | undefined;
    if (!turn || turn.status !== "editing") return;
    if (!turn.draft.regionId) {
      toast({ title: "Region required", description: "Please select a region.", variant: "destructive" });
      return;
    }
    if (!turn.draft.clientType) {
      toast({ title: "Client type required", description: "Please select a client type.", variant: "destructive" });
      return;
    }
    if (assignmentOptions && assignmentOptions.talent.length > 1 && !turn.draft.talentId) {
      toast({ title: "Talent required", description: "Please select a talent.", variant: "destructive" });
      return;
    }
    if (assignmentOptions && !assignmentOptions.isSalesRep && !turn.draft.assignedRepUserId) {
      toast({ title: "Sales rep required", description: "Please assign a sales rep.", variant: "destructive" });
      return;
    }
    patchScreenshotTurn(turnId, { status: "importing" });
    const pick = {
      fields: turn.draft.fields,
      notesIncludes: turn.draft.notesIncludes.filter((r) => r.value.trim().length > 0),
      idempotencyKey: turn.draft.idempotencyKey,
      regionId: turn.draft.regionId,
      clientType: turn.draft.clientType,
      talentId: turn.draft.talentId || undefined,
      assignedRepUserId: turn.draft.assignedRepUserId || undefined,
    };
    try {
      const r = await apiRequest("POST", "/api/voice-assistant/screenshot/import", {
        sessionId: session.sessionId,
        pick,
        attachmentId: turn.draft.attachmentId,
      });
      const { lead } = await r.json();
      patchScreenshotTurn(turnId, { status: "imported", createdLeadId: lead?.id });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Lead imported", description: lead?.contactName || lead?.leadName || "New lead created." });
    } catch (err: any) {
      patchScreenshotTurn(turnId, { status: "editing", error: err?.message ?? "Import failed." });
      toast({ title: "Couldn't import", description: err?.message ?? "Try again.", variant: "destructive" });
    }
  }

  // While loading, show nothing. Once loaded, FAB always renders on desktop
  // so admins can always reach the assistant regardless of config state.
  // The panel itself is gated on isVisible.
  if (isLoading) return null;

  // Hide the widget entirely on help / knowledge-base pages — the help
  // chat widget owns that surface and Clarik AI would be redundant.
  const onHelp = /(^|\/)help(-kb)?(\/|$|\?)/.test(location);
  if (onHelp) return null;

  // Task #116 / Task #117 — The floating orb shares a `layoutId` with two
  // possible morph targets, depending on which page the user is on:
  //   • Master Schedule → the Tour Planning drawer (`tour-panel-open`)
  //   • Calendar Hub    → the Calendar Hub side panel (Task #117)
  // The two routes are mutually exclusive, so we reuse a single shared id
  // (`ai-assistant-tour-morph`, kept as-is for backwards compatibility with
  // the tour drawer that already references it) and just point it at
  // whichever target is currently mounted. When the assistant is disabled,
  // no morph source exists → neither side claims the id (panel also skips it).
  const canMorph = isVisible && !prefersReducedMotion;
  const morphLayoutId = canMorph ? "ai-assistant-tour-morph" : undefined;
  const morphTransition = prefersReducedMotion
    ? { duration: 0.18 }
    : { type: "spring" as const, stiffness: 320, damping: 32, mass: 0.9 };
  // Reduced-motion: drawer can't claim the id, so morph is gated on
  // assistantEnabled only (not canMorph), and the orb crossfades out via
  // AnimatePresence below to coordinate with the drawer's fade-in.
  // Bug fix: use calendarCoachTarget (not calendarPanelMounted) so the orb
  // only hides when the coach tab is actually active. If the side panel is open
  // on Lodging/Meetings/Conflicts with no coach target, calendarPanelMounted
  // was true but calendarCoachTarget was null → isEmbedded was false, orb
  // disappeared and no panel replaced it — assistant became unreachable.
  const morphActive = morphReady && isVisible && !isMobile && (
    (onMasterSchedule && tourPanelOpen) ||
    (onCalendars && !!calendarCoachTarget)
  );

  // ── Button label ──────────────────────────────────────────────────
  const btnLabel =
    sandboxEngaged && sandboxCount > 0
      ? `Open assistant (${sandboxCount} leads staged)`
      : pendingCount > 0
      ? `Open assistant (${pendingCount} pending)`
      : "Open assistant";

  // Task #117 — Embedded mode renders the panel into the Calendar Hub side
  // panel target instead of the floating drawer position. The FAB is
  // suppressed (morphActive) and the panel takes the embedded styling.
  const panelPositionStyle = isEmbedded
    ? { position: "absolute" as const, inset: 0, zIndex: 1 }
    : isMobile
    ? { position: "fixed" as const, bottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))", left: 0, right: 0, zIndex: 9999 }
    : { position: "fixed" as const, bottom: "1.5rem", right: "1.5rem", zIndex: 9999 };
  const panelClassName = isEmbedded
    ? "w-full h-full bg-background border rounded-xl shadow-sm flex flex-col overflow-hidden"
    : `${isMobile ? "w-full rounded-t-xl rounded-b-none border-x-0 border-b-0" : "w-[380px] max-w-[calc(100vw-2rem)] rounded-xl"} h-[520px] max-h-[calc(100dvh-6rem)] bg-background border shadow-2xl flex flex-col`;
  const showActivePanel = (open || isEmbedded) && isVisible;
  const panelTarget: Element = (isEmbedded && calendarCoachTarget) ? calendarCoachTarget : document.body;

  // Task #117 — Active-panel JSX, defined once and rendered in one of two
  // portal targets: the floating drawer position (document.body) when used
  // as the global Assistant, or the Calendar Hub coach target when embedded.
  // The motion.div carries the shared layoutId so framer-motion morphs the
  // FAB ↔ panel as one element.
  const renderActivePanel = () => (
    <motion.div
      layoutId={isEmbedded ? undefined : morphLayoutId}
      transition={morphTransition}
      data-testid={isEmbedded ? "panel-voice-assistant-embedded" : "panel-voice-assistant"}
      style={panelPositionStyle}
      className={panelClassName}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-medium" data-testid="text-voice-title">
                Clarik AI
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Task #117 — when embedded inside the Calendar Hub side panel,
                  there's no close button: the panel owns its own lifecycle. */}
              {!isEmbedded && (
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)}
                  aria-label={voiceEngaged ? "Minimize (session stays active)" : "Close"}
                  data-testid="button-close-voice-assistant" className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Task #117 — the Work/Coach toggle was removed; coach mode is
              context-driven (embedded in Calendar Hub ⇒ coach; everywhere
              else ⇒ work). */}

          {/* Transcript */}
          <ScrollArea className="flex-1 px-4 py-3">
            <div className="space-y-3" data-testid="list-voice-turns" ref={scrollRef}>
              {turns.map((t) => {
                if (t.speaker === "bulk-screenshot") {
                  const d = t.draft;
                  const isImporting = t.status === "importing";
                  const isImported = t.status === "imported";
                  const isCancelled = t.status === "cancelled";
                  const isFinal = isImported || isCancelled;
                  const canImport =
                    !!d.regionId &&
                    !!d.clientType &&
                    (assignmentOptions === null || assignmentOptions.talent.length <= 1 || !!d.talentId) &&
                    (assignmentOptions === null || assignmentOptions.isSalesRep || !!d.assignedRepUserId);
                  const noun = d.recordType === "client" ? "clients" : "leads";
                  return (
                    <div key={t.id} className="rounded-lg border bg-card p-3 space-y-2.5 text-left" data-testid={`card-bulk-screenshot-${t.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="rounded bg-primary/10 p-1">
                          <Users className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                            {d.rows.length} {noun} from screenshot
                            {isImported && (
                              <Badge className="bg-green-600 text-white text-[10px] px-1.5 py-0">
                                <Check className="w-3 h-3 mr-0.5" />
                                {t.importedCount === d.rows.length
                                  ? `All ${t.importedCount} imported`
                                  : `${t.importedCount} of ${d.rows.length} imported`}
                              </Badge>
                            )}
                            {isCancelled && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Cancelled</Badge>}
                          </div>
                          {t.summary && <p className="text-[11px] text-muted-foreground">{t.summary}</p>}
                        </div>
                      </div>

                      {!isFinal && (
                        <>
                          {/* Per-record field mapping cards — all extracted fields editable */}
                          <div className="space-y-2 max-h-72 overflow-y-auto pr-0.5">
                            {d.rows.map((row, i) => {
                              const f = row.fields;
                              const rowFields: Array<{ key: string; label: string; value: string | null | undefined }> = [
                                { key: "contactName",     label: "Contact name",  value: f.contactName },
                                { key: "organizationName",label: "Organization",  value: f.organizationName },
                                { key: "emailAddress",    label: "Email",         value: f.emailAddress },
                                { key: "phoneNumber",     label: "Phone",         value: f.phoneNumber },
                                { key: "address",         label: "Address",       value: f.address },
                                { key: "city",            label: "City",          value: f.city },
                                { key: "state",           label: "State",         value: f.state },
                                { key: "zipCode",         label: "ZIP",           value: f.zipCode },
                                { key: "venueType",       label: "Venue type",    value: f.venueType },
                              ];
                              return (
                                <div key={i} className="rounded border bg-muted/10 overflow-hidden">
                                  <div className="bg-muted/40 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground border-b">
                                    Record {i + 1}
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 p-1.5">
                                    {rowFields.map(({ key, label, value }) => (
                                      <div key={key}>
                                        <div className="text-[9px] text-muted-foreground leading-none mb-0.5">{label}</div>
                                        <input
                                          className="w-full bg-background border border-input rounded px-1.5 py-0.5 text-[11px] outline-none focus:ring-1 focus:ring-primary/40"
                                          value={value ?? ""}
                                          placeholder="—"
                                          disabled={isImporting}
                                          onChange={(e) => updateBulkRowField(t.id, i, key, e.target.value)}
                                          data-testid={`input-bulk-row-${key}-${t.id}-${i}`}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Record type + shared assignment */}
                          <div className="space-y-2">
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Import as</Label>
                              <Select value={d.recordType} onValueChange={(v) => updateBulkRecordType(t.id, v as "lead" | "client")} disabled={isImporting}>
                                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-bulk-recordtype-${t.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="lead">Leads</SelectItem>
                                  <SelectItem value="client">Clients</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Region <span className="text-destructive">*</span></Label>
                              <Select value={d.regionId} onValueChange={(v) => updateBulkAssignment(t.id, "regionId", v)} disabled={isImporting}>
                                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-bulk-region-${t.id}`}>
                                  <SelectValue placeholder="Select region…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {assignmentOptions?.regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Client type <span className="text-destructive">*</span></Label>
                              <Select value={d.clientType} onValueChange={(v) => updateBulkAssignment(t.id, "clientType", v)} disabled={isImporting}>
                                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-bulk-clienttype-${t.id}`}>
                                  <SelectValue placeholder="Select client type…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {assignmentOptions?.clientTypes.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            {assignmentOptions && assignmentOptions.talent.length > 1 && (
                              <div>
                                <Label className="text-[11px] text-muted-foreground">Talent <span className="text-destructive">*</span></Label>
                                <Select value={d.talentId} onValueChange={(v) => updateBulkAssignment(t.id, "talentId", v)} disabled={isImporting}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-bulk-talent-${t.id}`}>
                                    <SelectValue placeholder="Select talent…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {assignmentOptions.talent.map((ta) => <SelectItem key={ta.id} value={ta.id}>{ta.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {assignmentOptions && !assignmentOptions.isSalesRep && assignmentOptions.salesReps.length > 0 && (
                              <div>
                                <Label className="text-[11px] text-muted-foreground">Assigned rep <span className="text-destructive">*</span></Label>
                                <Select value={d.assignedRepUserId} onValueChange={(v) => updateBulkAssignment(t.id, "assignedRepUserId", v)} disabled={isImporting}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-bulk-rep-${t.id}`}>
                                    <SelectValue placeholder="Select rep…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {assignmentOptions.salesReps.map((r) => <SelectItem key={r.userId} value={r.userId}>{r.fullName}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          {t.error && (
                            <p className="text-[11px] text-destructive flex items-center gap-1">
                              <AlertCircle className="w-3 h-3 shrink-0" />{t.error}
                            </p>
                          )}
                          <div className="flex gap-1.5 justify-end">
                            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => patchBulkScreenshotTurn(t.id, { status: "cancelled" })} disabled={isImporting} data-testid={`button-bulk-screenshot-cancel-${t.id}`}>
                              <X className="w-3 h-3 mr-1" />Cancel
                            </Button>
                            <Button type="button" size="sm" className="h-7 text-xs" onClick={() => importBulkScreenshotTurn(t.id)} disabled={isImporting || !canImport} data-testid={`button-bulk-screenshot-import-${t.id}`}>
                              {isImporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                              Import {d.rows.length} {noun}
                            </Button>
                          </div>
                        </>
                      )}
                      {isImported && (
                        <a href={`/${noun}`} className="text-[11px] text-primary underline" data-testid={`link-bulk-imported-${t.id}`}>
                          View {noun} →
                        </a>
                      )}
                      {isImported && t.failedRows && t.failedRows.length > 0 && (
                        <div className="space-y-0.5">
                          <p className="text-[11px] text-amber-600 font-medium flex items-center gap-1">
                            <AlertCircle className="w-3 h-3 shrink-0" />{t.failedRows.length} record{t.failedRows.length !== 1 ? "s" : ""} couldn't be imported:
                          </p>
                          {t.failedRows.map((fr) => (
                            <p key={fr.index} className="text-[10px] text-muted-foreground pl-4">Row {fr.index + 1}: {fr.error}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                if (t.speaker === "screenshot") {
                  const d = t.draft;
                  const isImporting = t.status === "importing";
                  const isImported = t.status === "imported";
                  const isCancelled = t.status === "cancelled";
                  const isFinal = isImported || isCancelled;
                  const canImport =
                    !!d.regionId &&
                    !!d.clientType &&
                    (assignmentOptions === null || assignmentOptions.talent.length <= 1 || !!d.talentId) &&
                    (assignmentOptions === null || assignmentOptions.isSalesRep || !!d.assignedRepUserId);
                  return (
                    <div key={t.id} className="rounded-lg border bg-card p-3 space-y-2.5 text-left" data-testid={`card-screenshot-mapping-${t.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="rounded bg-primary/10 p-1">
                          <ImageIcon className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                            Lead from screenshot
                            {isImported && <Badge className="bg-green-600 text-white text-[10px] px-1.5 py-0"><Check className="w-3 h-3 mr-0.5" />Imported</Badge>}
                            {isCancelled && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Cancelled</Badge>}
                          </div>
                          {t.summary && <p className="text-[11px] text-muted-foreground">{t.summary}</p>}
                        </div>
                      </div>
                      {!isFinal && (
                        <>
                          <div className="space-y-2">
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Region <span className="text-destructive">*</span></Label>
                              <Select value={d.regionId} onValueChange={(v) => updateScreenshotAssignment(t.id, "regionId", v)} disabled={isImporting}>
                                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-widget-region-${t.id}`}>
                                  <SelectValue placeholder="Select region…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {assignmentOptions?.regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[11px] text-muted-foreground">Client type <span className="text-destructive">*</span></Label>
                              <Select value={d.clientType} onValueChange={(v) => updateScreenshotAssignment(t.id, "clientType", v)} disabled={isImporting}>
                                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-widget-clienttype-${t.id}`}>
                                  <SelectValue placeholder="Select client type…" />
                                </SelectTrigger>
                                <SelectContent>
                                  {assignmentOptions?.clientTypes.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            {assignmentOptions && assignmentOptions.talent.length > 1 && (
                              <div>
                                <Label className="text-[11px] text-muted-foreground">Talent <span className="text-destructive">*</span></Label>
                                <Select value={d.talentId} onValueChange={(v) => updateScreenshotAssignment(t.id, "talentId", v)} disabled={isImporting}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-widget-talent-${t.id}`}>
                                    <SelectValue placeholder="Select talent…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {assignmentOptions.talent.map((ta) => <SelectItem key={ta.id} value={ta.id}>{ta.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {assignmentOptions && !assignmentOptions.isSalesRep && assignmentOptions.salesReps.length > 0 && (
                              <div>
                                <Label className="text-[11px] text-muted-foreground">Assigned rep <span className="text-destructive">*</span></Label>
                                <Select value={d.assignedRepUserId} onValueChange={(v) => updateScreenshotAssignment(t.id, "assignedRepUserId", v)} disabled={isImporting}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`select-widget-rep-${t.id}`}>
                                    <SelectValue placeholder="Select rep…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {assignmentOptions.salesReps.map((r) => <SelectItem key={r.userId} value={r.userId}>{r.fullName}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          {t.error && <p className="text-[11px] text-destructive">{t.error}</p>}
                          <div className="flex gap-1.5 justify-end">
                            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => patchScreenshotTurn(t.id, { status: "cancelled" })} disabled={isImporting} data-testid={`button-widget-screenshot-cancel-${t.id}`}>
                              <X className="w-3 h-3 mr-1" />Cancel
                            </Button>
                            <Button type="button" size="sm" className="h-7 text-xs" onClick={() => importScreenshotTurn(t.id)} disabled={isImporting || !canImport} data-testid={`button-widget-screenshot-import-${t.id}`}>
                              {isImporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                              Import
                            </Button>
                          </div>
                        </>
                      )}
                      {isImported && t.createdLeadId && (
                        <a href={`/leads?focus=${t.createdLeadId}`} className="text-[11px] text-primary underline" data-testid={`link-widget-imported-lead-${t.id}`}>View lead →</a>
                      )}
                    </div>
                  );
                }
                return (
                  <div key={t.id}
                    className={t.speaker === "user" ? "text-right" : t.speaker === "system" ? "text-center text-xs text-muted-foreground" : "text-left"}
                    data-testid={`row-voice-turn-${t.speaker}`}>
                    <span className={
                      t.speaker === "user"
                        ? "inline-block bg-primary text-primary-foreground rounded-lg px-3 py-2 max-w-[85%] text-sm"
                        : t.speaker === "system" ? ""
                        : "inline-block bg-muted rounded-lg px-3 py-2 max-w-[85%] text-sm"
                    }>
                      {t.text}
                    </span>
                    {t.speaker === "system" && t.link && (
                      <div className="mt-1">
                        <a
                          href={t.link.href}
                          className="text-[11px] text-primary underline hover:text-primary/80"
                          data-testid="link-coach-check-in"
                        >
                          {t.link.label}
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
              {(busy || extracting) && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" /> {extracting ? "Reading screenshot…" : "Thinking…"}
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          </ScrollArea>

          {/* Controls */}
          <div className="border-t px-3 py-3 space-y-2 shrink-0">
            {/* Text input always available */}
            <form onSubmit={(e) => { e.preventDefault(); void submitTyped(); }} className="flex gap-2">
              <Input value={typed} onChange={(e) => setTyped(e.target.value)}
                placeholder="Type a question…"
                disabled={busy || extracting} data-testid="input-voice-typed" />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv,text/markdown,application/json"
                className="hidden"
                onChange={handleFilePicked}
                data-testid="input-widget-file"
              />
              {session && (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={openFilePicker}
                  disabled={busy || extracting}
                  aria-label="Upload a screenshot of a lead"
                  data-testid="button-widget-upload"
                >
                  {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                </Button>
              )}
              <Button type="submit" size="icon" disabled={busy || extracting || !typed.trim()} data-testid="button-send-typed">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
    </motion.div>
  );

  return (
    <>
      {createPortal(
    <>
      {/* ── Floating button — always visible on desktop at bottom-right ── */}
      {/* Task #116 — when morphActive, the Tour Planning drawer renders the
          shared layoutId target instead so Framer Motion can morph orb↔drawer
          as one continuous element. In reduced-motion mode there is no shared
          layoutId on either side; AnimatePresence below crossfades the orb
          out so it coordinates with the drawer's fade-in. */}
      <AnimatePresence>
      {!open && !isMobile && !morphActive && (
        <motion.button
          key="ai-orb"
          layoutId={morphLayoutId}
          transition={morphTransition}
          initial={prefersReducedMotion ? { opacity: 0 } : false}
          animate={prefersReducedMotion ? { opacity: 1 } : undefined}
          exit={prefersReducedMotion ? { opacity: 0, transition: { duration: 0.18 } } : { opacity: 1, transition: { duration: 0 } }}
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => {
            // On Calendar Hub, expand the side panel to the Coach tab instead
            // of opening the floating drawer. Falls back to the floating drawer
            // if the panel callback hasn't been registered yet.
            if (onCalendars && openCalendarCoachPanel) {
              openCalendarCoachPanel();
            } else {
              setOpen(true);
            }
          }}
          aria-label={btnLabel}
          data-testid="button-open-voice-assistant"
          style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 9999, borderRadius: 9999 }}
          className={[
            "h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 flex items-center justify-center relative transition-shadow",
            sandboxEngaged && sandboxCount > 0
              ? "ring-2 ring-primary ring-offset-2 ring-offset-background animate-pulse"
              : "",
          ].filter(Boolean).join(" ")}
        >
          <Sparkles className="w-6 h-6" />

          {/* Sandbox badge */}
          {sandboxEngaged && sandboxCount > 0 && (
            <span data-testid="badge-sandbox-count" aria-hidden="true"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-semibold flex items-center justify-center shadow ring-2 ring-background">
              {sandboxCount > 99 ? "99+" : sandboxCount}
            </span>
          )}
          {/* Coach pings badge */}
          {!(sandboxEngaged && sandboxCount > 0) && pendingCount > 0 && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span data-testid="badge-coach-pending-pings" aria-label={pendingCount === 1 ? "Coach check-in pending" : `${pendingCount} coach check-ins pending`}
                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center shadow ring-2 ring-background">
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  {pendingCount === 1 ? "Coach check-in pending" : `${pendingCount} coach check-ins pending`}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </motion.button>
      )}
      </AnimatePresence>

      {/* ── Chat panel — disabled state (FAB-only flow) ── */}
      {open && !isVisible && (
        <div data-testid="panel-voice-assistant-disabled"
          style={isMobile ? { position: "fixed", bottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))", left: 0, right: 0, zIndex: 9999 } : { position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 9999 }}
          className={`${isMobile ? "w-full rounded-t-xl rounded-b-none border-x-0 border-b-0" : "w-[380px] max-w-[calc(100vw-2rem)] rounded-xl"} bg-background border shadow-2xl flex flex-col`}>
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-medium">Clarik AI</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)} className="h-8 w-8" data-testid="button-close-voice-assistant">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
            <Sparkles className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-sm font-medium">Clarik AI not enabled</p>
            <p className="text-xs text-muted-foreground">An admin can turn it on in <strong>Admin Panel → AI Settings</strong>.</p>
          </div>
        </div>
      )}
      {/* Task #117 — active panel. When NOT embedded, renders here inside
          document.body. When embedded, the same JSX is portaled to the
          Calendar Hub coach target via a second createPortal below. The
          panel body is identical in both modes; only positioning differs. */}
      {showActivePanel && !isEmbedded && renderActivePanel()}
      {/* Task #117 — when embedded, the active panel is portaled into the
          Calendar Hub coach target instead of document.body. */}
    </>,
        document.body
      )}
      {showActivePanel && isEmbedded && calendarCoachTarget && createPortal(renderActivePanel(), calendarCoachTarget)}
    </>
  );
}

function hello(provider: ProviderKind, role: string): string {
  if (provider === "realtime") {
    return `Hi — I'm Clarik AI. Connected and listening. Close this panel and keep working.`;
  }
  return `Hi — I'm Clarik AI. Type a question below.`;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => {
      const dataUrl = String(r.result ?? "");
      const idx = dataUrl.indexOf(",");
      resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
    };
    r.readAsDataURL(blob);
  });
}

// ---------------------------------------------------------------------------
// Realtime engine (WebRTC)
// ---------------------------------------------------------------------------

interface RealtimeStartOpts {
  sessionId: string;
  onTranscript: (text: string, who: "user" | "assistant") => void;
  onError: (msg: string) => void;
}
interface RealtimeHandle { stop: () => void; }

async function startRealtimeEngine(opts: RealtimeStartOpts): Promise<RealtimeHandle> {
  const tokenRes = await apiRequest("POST", "/api/voice-assistant/realtime/token", { sessionId: opts.sessionId });
  const tokenJson = await tokenRes.json();
  const ephemeral = tokenJson?.client_secret?.value;
  if (!ephemeral) throw new Error("missing realtime token");

  const pc = new RTCPeerConnection();
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => pc.addTrack(track, stream));

  const remote = new MediaStream();
  pc.ontrack = (event) => {
    event.streams[0].getTracks().forEach((t) => remote.addTrack(t));
    const audio = new Audio();
    audio.srcObject = remote;
    audio.autoplay = true;
    audio.play().catch(() => {});
  };

  const dc = pc.createDataChannel("oai-events");
  dc.onmessage = async (msg) => {
    let evt: any;
    try { evt = JSON.parse(msg.data); } catch { return; }

    if (evt?.type === "response.done") {
      const usage = evt?.response?.usage;
      if (usage) {
        apiRequest("POST", "/api/voice-assistant/realtime/record-usage", {
          sessionId: opts.sessionId,
          inputAudioTokens:  Number(usage?.input_token_details?.audio_tokens  ?? 0),
          outputAudioTokens: Number(usage?.output_token_details?.audio_tokens ?? 0),
          inputTextTokens:   Number(usage?.input_token_details?.text_tokens   ?? 0),
          outputTextTokens:  Number(usage?.output_token_details?.text_tokens  ?? 0),
          cachedTokens:      Number(usage?.input_token_details?.cached_tokens ?? 0),
        }).catch(() => {});
      }
    } else if (evt?.type === "response.audio_transcript.done") {
      opts.onTranscript(String(evt.transcript ?? ""), "assistant");
    } else if (evt?.type === "conversation.item.input_audio_transcription.completed") {
      opts.onTranscript(String(evt.transcript ?? ""), "user");
    } else if (evt?.type === "response.function_call_arguments.done") {
      try {
        const argsObj = JSON.parse(evt.arguments || "{}");
        const proxied = await apiRequest("POST", "/api/voice-assistant/chat/respond", {
          sessionId: opts.sessionId,
          userText: `[realtime tool bridge] tool=${evt.name} args=${JSON.stringify(argsObj)}`,
          history: [],
          coachModeOverride: false,
          surface: activeSurface,
          currentView: currentViewRef.current,
        });
        const proxyJson = await proxied.json();
        dc.send(JSON.stringify({
          type: "conversation.item.create",
          item: { type: "function_call_output", call_id: evt.call_id, output: JSON.stringify(proxyJson?.toolResult ?? proxyJson ?? {}) },
        }));
        dc.send(JSON.stringify({ type: "response.create" }));
      } catch {}
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // GA endpoint — the beta `/v1/realtime?model=...` URL and the
  // `gpt-4o-realtime-preview-*` model were retired in late 2025.
  const sdpRes = await fetch(
    "https://api.openai.com/v1/realtime/calls?model=gpt-realtime",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${ephemeral}`, "Content-Type": "application/sdp" },
      body: offer.sdp,
    },
  );
  if (!sdpRes.ok) throw new Error(`SDP exchange failed: ${sdpRes.status}`);
  const sdpAnswer = await sdpRes.text();
  await pc.setRemoteDescription({ type: "answer", sdp: sdpAnswer });

  return {
    stop() { stream.getTracks().forEach((t) => t.stop()); pc.close(); },
  };
}
